import React from 'react';
import PropTypes from 'prop-types';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

/**
 * selectedSkuSizeSchema - We only want to display model size information
 * if the current sku is of the same size schema of size data we have for the
 * model. We want to display the same type of size for the model as is the item.
 * So we need to determine, based on the 'size' string and the businessLine,
 * what the size schema is.
 *
 * @param selectedSkuSize
 * @param businessLine
 * @returns {'alphaSizeName'|'dress'|'waist'|null}
 */
export const selectedSkuSizeSchema = (selectedSkuSize, businessLine) => {
  // We expect an alphaSizeName & selectedSkuSize to always exist
  // the lack of one would indicate incomplete or corrupted data.
  if (!selectedSkuSize || !businessLine) {
    return null;
  }

  const alphaSizeKeyLetters = ['S', 'M', 'L', 'X'];

  const isAlphaSize = alphaSizeKeyLetters.some(keyLetter => {
    return selectedSkuSize.toUpperCase().includes(keyLetter);
  });

  if (isAlphaSize) {
    return 'alphaSizeName';
  }

  // If it is not an alpha size, we can deduce that for mens' lines it will be
  // 'waist', and 'dress' for womens'
  if (businessLine.toUpperCase() === 'WOMENS') {
    return 'dress';
  }

  if (businessLine.toUpperCase() === 'MENS') {
    return 'waist';
  }

  return null;
};

const ModelSize = ({ modelSize }) => {
  const [state] = useProductDetailsPageContext();
  const selectedSkuSize =
    state.selectedVariant?.size?.name || state.fallbackVariant?.size?.name;
  const businessLine = state.productBusinessLine || null;
  const sizeSchema = selectedSkuSizeSchema(selectedSkuSize, businessLine);

  if (!sizeSchema || !modelSize[sizeSchema]) {
    return null;
  }

  const formattedModelHeight = () => {
    const feetFromInches = Math.floor(modelSize.height / 12);
    const inchesRemainder = modelSize.height % 12;

    return `${feetFromInches}’${inchesRemainder}”`;
  };

  const sizeType = sizeSchema === 'waist' ? 'waist ' : '';

  return (
    <span data-testid="on-figure-spec-display">
      {`Model is ${sizeType}size `}
      <strong>{modelSize[sizeSchema]}</strong>
      {modelSize.height && (
        <>
          {` | Height `}
          <strong>{formattedModelHeight()}</strong>
        </>
      )}
    </span>
  );
};

ModelSize.propTypes = {
  modelSize: PropTypes.shape({
    height: PropTypes.string,
    waist: PropTypes.string,
    alphaSizeName: PropTypes.string,
    dress: PropTypes.string,
  }).isRequired,
};

export default ModelSize;
